import React from 'react';
import { Tooltip } from 'antd';
import { FN210 } from '../../../services/n211Api';
import { Mode } from '../hooks/useN211Mode';

interface N211Permissions {
  canView: boolean;
  canCreate: boolean;
  canEdit: boolean;
  canDelete: boolean;
  canExport: boolean;
  canPrint: boolean;
  canApprove: boolean;
  hasAnyPermission: boolean;
}

interface ActionBarProps {
  mode: Mode;
  isEditing: boolean;
  record: FN210 | null;
  currentIdx: number;
  allIdsLength: number;
  permissions: N211Permissions;
  onAdd: () => void;
  onModify: () => void;
  onDelete: () => void;
  onNavTo: (idx: number) => void;
  onExit: () => void;
}

/** Wrapper button: shows tooltip when permission denied */
const PermBtn: React.FC<{
  allowed: boolean;
  disabled: boolean;
  className?: string;
  title?: string;
  onClick?: () => void;
  children: React.ReactNode;
}> = ({ allowed, disabled, className, title, onClick, children }) => {
  const btn = (
    <button
      className={className || 'btn'}
      disabled={disabled || !allowed}
      title={title}
      onClick={onClick}
    >
      {children}
    </button>
  );
  if (!allowed) {
    return <Tooltip title="權限不足">{btn}</Tooltip>;
  }
  return btn;
};

const ActionBar: React.FC<ActionBarProps> = React.memo(({
  mode, isEditing, record, currentIdx, allIdsLength,
  permissions, onAdd, onModify, onDelete, onNavTo, onExit,
}) => {
  const isApproved = record?.fn210_80 === 'P';

  return (
    <div className="action-bar">
      {/* Navigation group */}
      <div className="action-group">
        <PermBtn allowed={permissions.canView} disabled={isEditing || currentIdx <= 0}
          onClick={() => onNavTo(0)} title="最前">
          {'|<'}
        </PermBtn>
        <PermBtn allowed={permissions.canView} disabled={isEditing || currentIdx <= 0}
          onClick={() => onNavTo(currentIdx - 1)} title="上一筆">
          {'<'}
        </PermBtn>
        <PermBtn allowed={permissions.canView} disabled={isEditing || currentIdx >= allIdsLength - 1}
          onClick={() => onNavTo(currentIdx + 1)} title="下一筆">
          {'>'}
        </PermBtn>
        <PermBtn allowed={permissions.canView} disabled={isEditing || currentIdx >= allIdsLength - 1}
          onClick={() => onNavTo(allIdsLength - 1)} title="最後">
          {'>|'}
        </PermBtn>
      </div>

      {/* CRUD group */}
      <div className="action-group">
        <PermBtn
          allowed={mode === 'add' ? permissions.canCreate : mode === 'edit' ? permissions.canEdit : permissions.canCreate}
          disabled={isEditing ? false : isApproved}
          onClick={onAdd}
          title={isEditing ? '存檔' : '新增'}
        >
          {isEditing ? '存檔' : '新增'}
        </PermBtn>
        <PermBtn
          allowed={permissions.canEdit}
          disabled={!isEditing && (!record || isApproved)}
          onClick={onModify}
          title={isEditing ? '還原' : '修改'}
        >
          {isEditing ? '還原' : '修改'}
        </PermBtn>
        <PermBtn
          allowed={permissions.canDelete}
          disabled={isEditing || !record || isApproved}
          onClick={onDelete}
          title="刪除"
        >
          刪除
        </PermBtn>
      </div>

      {/* Exit */}
      <div className="action-group">
        <button className="btn" onClick={onExit} title="離開">離開</button>
      </div>

      {/* Status badge */}
      {isApproved && (
        <span className="status-badge approved">已核准</span>
      )}
    </div>
  );
});

ActionBar.displayName = 'ActionBar';
export default ActionBar;
